/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import template from './player-comparison.stache';
import FeedService from 'scripts/services/base-feed-service';
import can from 'can';
import _ from 'lodash';
import appContext from 'scripts/app-context';
import PlayerService from 'scripts/services/player-service';

const courseStatisticsPageUrl =
    window.globalSettings.pagePaths.courseStatistics;
const playerStatisticsPageUrl =
    window.globalSettings.pagePaths.playerStatistics;
const statisticsFeedUrl = window.globalSettings.feeds.smt.playerStatisticsUrl;
const statisticsSponsor =
    window.globalSettings.statisticsSettings.playerStatsSponsor;

const kpiMapping = new can.Map({
    total: 'Total',
    'off-the-tee': 'Strokes Gained Off the Tee',
    approach: 'Strokes Gained Approach',
    'short-game': 'Strokes Gained Short Game',
    'strokes-putting': 'Strokes Gained Putting',
    'driving-distance': 'Driving Distance',
    'fairways-hit': 'Fairways Hit',
    'greens-in-regulation': 'Greens in Regulation',
    putting: 'Number of Putts',
    'sand-saves': 'Sand Saves',
    birdies: 'Birdies',
    eagles: 'Eagles',
    'missed-fairway-penalty': 'Missed Fairway',
});

const statsFilter = [
    { title: 'Driving Distance', value: 'driving-distance' },
    { title: 'Fairways Hit', value: 'fairways-hit' },
    { title: 'Missed Fairway Penalty', value: 'missed-fairway-penalty' },
    { title: 'Greens in Regulation', value: 'greens-in-regulation' },
    { title: 'Putting', value: 'putting' },
    { title: 'Sand Saves', value: 'sand-saves' },
    { title: 'Birdies', value: 'birdies' },
    { title: 'Eagles', value: 'eagles' },
];

const statFilters = [
    { title: 'total', value: 'total' },
    { title: 'off the tee', value: 'off-the-tee' },
    { title: 'approach', value: 'approach' },
    { title: 'around the green', value: 'short-game' },
    { title: 'putting', value: 'strokes-putting' },
];

const strokesGained = {
    title: 'Strokes Gained',
    value: 'strokes-gained',
};

const defaultRoundFilter = { title: 'All Rounds', value: 'all' };

const roundFilters = [
    { title: 'Round 1', value: '1' },
    { title: 'Round 2', value: '2' },
    { title: 'Round 3', value: '3' },
    { title: 'Round 4', value: '4' },
];

const ROUTE_PLAYER_1 = 'player1';
const ROUTE_PLAYER_2 = 'player2';
const ROUTE_COMPARISON = 'player1=:player1&player2=:player2';

const getStatValue = (stats, path) => {
    const value = _.get(stats, `${path}.displayValue`);
    return isNaN(parseFloat(value)) ? '-' : value;
};
const parseStat = (stats, path) => parseFloat(getStatValue(stats, path));

Component.extend({
    tag: 'player-comparison',
    template: template,
    leakScope: false,
    viewModel: {
        courseStatisticsPageUrl: courseStatisticsPageUrl,
        playerStatisticsPageUrl: playerStatisticsPageUrl,
        sponsor: statisticsSponsor,
        define: {
            isFetching: {
                type: 'boolean',
                value: true,
            },
            isMobile: {
                get() {
                    return (
                        ['small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
            isMobileOrTablet: {
                get() {
                    return (
                        ['large', 'medium', 'small', 'extra-small'].indexOf(
                            appContext.model.screen.attr('screenSize'),
                        ) > -1
                    );
                },
            },
            statFilter: {
                type: 'string',
                value: strokesGained.value,
            },
            roundFilter: {
                type: 'string',
                value: defaultRoundFilter.value,
            },
            isModalActive: {
                type: 'boolean',
                value: false,
            },
            statTabs: {
                get() {
                    return [strokesGained, ...statsFilter];
                },
            },
            roundTabs: {
                get() {
                    if (!this.isStrokesGainedActive()) {
                        return null;
                    }

                    return [defaultRoundFilter, ...roundFilters];
                },
            },
            firstPlayerId: {
                type: 'string',
                value: null,
            },
            secondPlayerId: {
                type: 'string',
                value: null,
            },
            playersList: {
                Type: can.List,
                Value: can.List,
            },
            firstPlayerInfo: {
                Type: can.Map,
                Value: can.Map,
            },
            secondPlayerInfo: {
                Type: can.Map,
                Value: can.Map,
            },
            showComparisonPill: {
                type: 'boolean',
                value: false,
            },
            selectedPlayers: {
                get() {
                    return [
                        this.attr('firstPlayerId'),
                        this.attr('secondPlayerId'),
                    ];
                },
            },
            isDataNotAvailable: {
                type: 'boolean',
                value: false,
            },
        },
        isStrokesGainedActive() {
            return this.attr('statFilter') === strokesGained.value;
        },
        getRank(stats) {
            const rankFilter = this.isStrokesGainedActive()
                ? `stats.total.${this.roundFilter}`
                : `stats.${this.statFilter}.all`;

            return {
                rank: _.get(stats, `${rankFilter}.rank`) || '-',
                rankOrder: _.get(stats, `${rankFilter}.rankOrder`) || '-',
            };
        },

        resetFirstPlayer(event) {
            event.stopPropagation();
            this.attr('firstPlayerId', null);
            this.openModal();
            can.route.removeAttr(ROUTE_PLAYER_1);
        },

        resetSecondPlayer(event) {
            event.stopPropagation();
            this.attr('secondPlayerId', null);
            this.openModal();
            can.route.removeAttr(ROUTE_PLAYER_2);
        },

        onSelectPlayer(context, element, type, playerId) {
            if (!this.attr('firstPlayerId')) {
                this.attr('firstPlayerId', playerId);
            } else if (!this.attr('secondPlayerId')) {
                this.attr('secondPlayerId', playerId);
            }
        },

        onResetPlayer(context, element, type, playerId) {
            if (this.attr('firstPlayerId') === playerId) {
                this.attr('firstPlayerId', null);
            } else if (this.attr('secondPlayerId') === playerId) {
                this.attr('secondPlayerId', null);
            }
        },

        onApplyComparison() {
            can.route.attr(ROUTE_PLAYER_1, this.attr('firstPlayerId'));
            can.route.attr(ROUTE_PLAYER_2, this.attr('secondPlayerId'));
            this.onModalClose();
        },

        openModal() {
            this.attr('isModalActive', true);
            return (
                this.attr('isMobileOrTablet') &&
                appContext.model.dispatch('page-scroll-toggle', [true])
            );
        },

        onModalClose() {
            this.attr('isModalActive', false);
            return (
                this.attr('isMobileOrTablet') &&
                appContext.model.dispatch('page-scroll-toggle', [false])
            );
        },

        isSelectedPlayer(playerId) {
            return this.attr('isSelected')(playerId);
        },
    },
    events: {
        inserted() {
            this.registerRouteListener();
            this.initFeedService();
            this.registerModelListeners();
        },
        registerRouteListener() {
            can.route(ROUTE_COMPARISON);
            can.route.ready();
            can.route.bind(
                'change',
                (event, attributeName, type, attributeValue) => {
                    if (
                        !(type === 'set' || type === 'add' || type === 'remove')
                    ) {
                        return;
                    }

                    if (attributeName === ROUTE_PLAYER_1) {
                        this.viewModel.attr('firstPlayerId', attributeValue);
                    } else if (attributeName === ROUTE_PLAYER_2) {
                        this.viewModel.attr('secondPlayerId', attributeValue);
                    }
                },
            );

            const routeParams = can.route.attr();
            this.viewModel.attr('firstPlayerId', routeParams.player1);
            this.viewModel.attr('secondPlayerId', routeParams.player2);

            if (!routeParams.player1 || !routeParams.player2) {
                return this.viewModel.openModal();
            }
        },
        registerModelListeners() {
            can.compute(() => ({
                statFilter: this.viewModel.attr('statFilter'),
                roundFilter: this.viewModel.attr('roundFilter'),
            })).bind('change', () => this.updateStats());

            can.compute(() => ({
                firstPlayerId: this.viewModel.attr('firstPlayerId'),
                secondPlayerId: this.viewModel.attr('secondPlayerId'),
            })).bind('change', () => {
                this.updatePlayersInfo();
                this.updateStats();
            });
        },
        findPlayer(id) {
            if (!id || !this.playerStats) {
                return;
            }

            return _.find(
                this.playerStats,
                ({ player }) => player.identifier === id,
            );
        },
        changePlayerInfo(playerId, initialData, key) {
            if (Boolean(playerId)) {
                return PlayerService.getPlayerInfo(
                    playerId,
                    (_, data) => this.viewModel.attr(key, data),
                    () => {},
                    () => {},
                    false,
                );
            }

            this.viewModel.attr(key, initialData);
        },
        updateStats() {
            const firstPlayerStats = this.findPlayer(
                this.viewModel.attr('firstPlayerId'),
            );
            const secondPlayerStats = this.findPlayer(
                this.viewModel.attr('secondPlayerId'),
            );
            const filter = this.viewModel.isStrokesGainedActive()
                ? this.viewModel.attr('roundFilter')
                : this.viewModel.attr('statFilter');
            const titles = this.viewModel.isStrokesGainedActive()
                ? statFilters
                : [...roundFilters, defaultRoundFilter];

            const columns = titles.map(({ value, title }) => {
                const statsPath = this.viewModel.isStrokesGainedActive()
                    ? `stats.${value}.${filter}.stat`
                    : `stats.${filter}.${value}.stat`;

                return {
                    title: title,
                    firstPlayer: {
                        value: getStatValue(firstPlayerStats, statsPath),
                        isBigger: this.comparePlayerStats(
                            firstPlayerStats,
                            secondPlayerStats,
                            statsPath,
                            title,
                        ),
                    },
                    secondPlayer: {
                        value: getStatValue(secondPlayerStats, statsPath),
                        isBigger: this.comparePlayerStats(
                            secondPlayerStats,
                            firstPlayerStats,
                            statsPath,
                            title,
                        ),
                    },
                };
            });

            const firstPlayerRank = this.viewModel.getRank(firstPlayerStats);
            const secondPlayerRank = this.viewModel.getRank(secondPlayerStats);
            const isSameRank = firstPlayerRank.rank === secondPlayerRank.rank;

            columns.unshift({
                title: 'rank',
                firstPlayer: {
                    value: firstPlayerRank.rank,
                    isBigger:
                        !isSameRank &&
                        this.comparePlayerStats(
                            secondPlayerRank.rankOrder,
                            firstPlayerRank.rankOrder,
                            'rank',
                        ),
                },
                secondPlayer: {
                    value: secondPlayerRank.rank,
                    isBigger:
                        !isSameRank &&
                        this.comparePlayerStats(
                            firstPlayerRank.rankOrder,
                            secondPlayerRank.rankOrder,
                            'rank',
                        ),
                },
            });

            this.viewModel.attr('columns', columns);
        },
        updatePlayersInfo() {
            const firstPlayerId = this.viewModel.attr('firstPlayerId');
            const secondPlayerId = this.viewModel.attr('secondPlayerId');
            const firstPlayerStats = this.findPlayer(firstPlayerId);
            const secondPlayerStats = this.findPlayer(secondPlayerId);

            this.changePlayerInfo(
                firstPlayerId,
                firstPlayerStats,
                'firstPlayerInfo',
            );
            this.changePlayerInfo(
                secondPlayerId,
                secondPlayerStats,
                'secondPlayerInfo',
            );
        },
        comparePlayerStats(firstStats, secondStats, path, title) {
            const hasInvertedStats = ['putting', 'missed-fairway-penalty'].some(
                (e) => e === title || e === this.viewModel.attr('statFilter'),
            );

            if (path === 'rank') {
                if ((firstStats === '-', secondStats === '-')) {
                    return false;
                }

                return firstStats > secondStats;
            }

            if (hasInvertedStats) {
                return (
                    parseStat(firstStats, path) < parseStat(secondStats, path)
                );
            }

            return parseStat(firstStats, path) > parseStat(secondStats, path);
        },
        initFeedService() {
            const getSortingData = (data = []) =>
                _.sortBy(
                    data.map((e) => _.get(e, 'standing.player')),
                    ['lastName', 'firstName'],
                );
            this.statsFeedService = new FeedService({
                feedUrl: statisticsFeedUrl,
                fromCache: false,
                autoUpdate: false,
            });

            this.statsFeedService
                .on('fetching', (ev, value) => {
                    this.viewModel.attr('isFetching', value);
                })
                .on('data', (ev, data) => {
                    const playerList = data.filter((statsRow) => {
                        const positionValue =
                            statsRow.standing.position.displayValue;
                        return (
                            positionValue.startsWith('T') ||
                            Boolean(Number(positionValue))
                        );
                    });

                    this.playerStats = this.getPlayerStats(playerList);
                    this.viewModel.attr(
                        'playersList',
                        getSortingData(playerList),
                    );
                    this.viewModel.attr(
                        'showComparisonPill',
                        playerList.length >= 2,
                    );
                    this.updatePlayersInfo();
                    this.updateStats();
                })
                .on('error', (error) => {
                    console.error('Error while retrieving player data', error);
                    this.viewModel.attr('isFetching', false);
                    this.viewModel.attr('isDataNotAvailable', true);
                });
            this.statsFeedService.fetch();
        },

        getPlayerStats(data) {
            return data.map((statsRow) => {
                const result = {
                    player: _.get(statsRow, 'standing.player'),
                    position: _.get(statsRow, 'standing.position'),
                    stats: {},
                };

                kpiMapping.each((kpiTitle, key) => {
                    result.stats[key] = {};
                    for (let i = 0; i < 4; i++) {
                        const roundInfo = _.get(
                            statsRow,
                            `roundsInfo.${i}.stats`,
                            [],
                        );
                        const flatRoundStats = this.flatStrokesStats(roundInfo);
                        const roundStats =
                            flatRoundStats
                                .filter((stat) => stat.name === kpiTitle)
                                .pop() || null;
                        _.set(result, `stats.${key}.${i + 1}`, roundStats);
                    }
                    const allRoundsInfo = _.get(statsRow, 'overallStats', []);
                    const flatAllRoundsStats = this.flatStrokesStats(
                        allRoundsInfo,
                    );
                    const allRoundsStats =
                        flatAllRoundsStats
                            .filter((stat) => stat.name === kpiTitle)
                            .pop() || null;
                    _.set(result, `stats.${key}.all`, allRoundsStats);
                });
                return result;
            });
        },
        flatStrokesStats(statData) {
            return statData.reduce((acc, roundInfo) => {
                if (roundInfo.name === strokesGained.title) {
                    roundInfo.sections.forEach((section) => acc.push(section));
                    return acc;
                }

                acc.push(roundInfo);
                return acc;
            }, []);
        },
        destroyFeedService() {
            if (this.statsFeedService) {
                this.statsFeedService.destroy();
                this.statsFeedService = null;
            }
        },
        removed() {
            this.destroyFeedService();
        },
    },
});
